/** The client cache: rows by id with their as_of, and views, in memory and on disk (design 16.8, rule U-36). */

import { clientLog } from "../clientLog";
import { collectionRange, metaKey, run, type RowRecord, rowKey, SCHEMA_KEY, type ScopeMeta, scopeRange, type ViewRecord, viewKey } from "./idb";

/** Change this number when the shape of a kept row or view changes: a mismatch drops the cache at boot. */
export const CACHE_SCHEMA = 1;
export const UNUSED_MS = 7 * 86_400_000;

interface Entry {
  row: unknown;
  as_of: number;
  json?: string;
}

export interface View<E = unknown> {
  collection: string;
  at: number;
  ids: string[];
  extra: E;
}

let scope: string | null = null;
let meta: ScopeMeta | null = null;
let rows = new Map<string, Map<string, Entry>>();
let views = new Map<string, View>();
let version = 0;
let booted: Promise<void> | null = null;
let loading: Promise<void> = Promise.resolve();
const listeners = new Set<() => void>();
const pending = new Set<Promise<unknown>>();
const closed = new Set<string>();
let waker: ((reason: string) => void) | null = null;

/** A cache line in the console and the same event for the server log: counts, ages and times only. */
export function note(name: string, text: string, fields: Record<string, string | number | boolean | null> = {}): void {
  console.info(text);
  clientLog.event(name, fields);
}

const errorName = (e: unknown) => (e instanceof Error ? e.name : "unknown");

export const scopeOf = (slug: string, uid: string) => `${slug}:${uid}`;
const slugOf = (key: string) => key.slice(0, key.lastIndexOf(":"));

function emit() {
  version += 1;
  for (const listener of listeners) listener();
}

export function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const getVersion = () => version;
export const currentScope = () => scope;

function track(work: Promise<unknown>) {
  pending.add(work);
  void work.finally(() => pending.delete(work));
}

function persist(label: string, work: Parameters<typeof run>[1], key: string | null) {
  if (key !== null && closed.has(key)) {
    note("cache_write_refused", `cache: refused the ${label} write after the sign-out drop`, { store: label });
    return;
  }
  track(
    run("readwrite", work).catch((e: unknown) => {
      note("cache_write_failed", `cache: the ${label} write failed (${errorName(e)}), memory only`, { store: label, error: errorName(e) });
    }),
  );
}

/** Like persist, but the caller waits for the disk; false when the write failed or was refused. */
function persistNow(label: string, work: Parameters<typeof run>[1], key: string): Promise<boolean> {
  if (closed.has(key)) return Promise.resolve(false);
  const done = run("readwrite", work).then(
    () => true,
    (e: unknown) => {
      note("cache_write_failed", `cache: the ${label} write failed (${errorName(e)}), memory only`, { store: label, error: errorName(e) });
      return false;
    },
  );
  track(done);
  return done;
}

/** Wait for each write to the disk; the auto update calls this before a reload. */
export async function flush(): Promise<void> {
  const started = performance.now();
  let writes = 0;
  while (pending.size) {
    writes += pending.size;
    await Promise.all([...pending]);
  }
  const ms = Math.round(performance.now() - started);
  if (writes) note("cache_flush", `cache: flushed ${writes} writes in ${ms} ms`, { writes, ms });
}

function deleteScope(stores: Parameters<Parameters<typeof run>[1]>[0], key: string) {
  stores.rows.delete(scopeRange(key));
  stores.views.delete(scopeRange(key));
  stores.meta.delete(metaKey(key));
}

/** Once per page load: drop the cache of an older CACHE_SCHEMA, and each scope unused for 7 days. */
async function boot(now: number): Promise<void> {
  const found = await run<{ schema: unknown; metas: ScopeMeta[] }>("readonly", (stores, done) => {
    const schema = stores.meta.get(SCHEMA_KEY);
    const metas = stores.meta.getAll(IDBKeyRange.bound("scope:", "scope:￿"));
    metas.onsuccess = () => done({ schema: schema.result, metas: metas.result as ScopeMeta[] });
  });
  if (!found) return;
  if (found.schema !== CACHE_SCHEMA) {
    await run("readwrite", (stores) => {
      if (found.schema !== undefined) {
        stores.meta.clear();
        stores.rows.clear();
        stores.views.clear();
      }
      stores.meta.put(CACHE_SCHEMA, SCHEMA_KEY);
    });
    if (found.schema !== undefined)
      note("cache_dropped", `cache: dropped all, schema ${String(found.schema)} is not ${CACHE_SCHEMA}`, { reason: "schema", from: String(found.schema), to: CACHE_SCHEMA });
    return;
  }
  const old = found.metas.filter((item) => now - item.used_at > UNUSED_MS);
  if (!old.length) return;
  await run("readwrite", (stores) => {
    for (const item of old) deleteScope(stores, item.scope);
  });
  for (const item of old) {
    const days = Math.floor((now - item.used_at) / 86_400_000);
    note("cache_dropped", `cache: dropped ${slugOf(item.scope)}, unused for ${days} days`, { reason: "unused", days });
  }
}

function forget() {
  rows = new Map();
  views = new Map();
}

/** Use the cache of one user in one organization; load its rows and views from the disk. */
export function openScope(slug: string, uid: string, now: number = Date.now()): Promise<void> {
  const next = scopeOf(slug, uid);
  if (scope === next) return loading;
  closed.delete(next);
  forget();
  scope = next;
  meta = { scope: next, used_at: now, roles: null, cursor: null };
  emit();
  const started = performance.now();
  loading = (async () => {
    booted ??= boot(now).catch((e: unknown) => {
      note("cache_no_disk", `cache: no disk cache (${errorName(e)}), memory only`, { error: errorName(e) });
    });
    await booted;
    const found = await run<{ meta?: ScopeMeta; rows: RowRecord[]; views: ViewRecord[] }>("readonly", (stores, done) => {
      const kept = stores.meta.get(metaKey(next));
      const keptRows = stores.rows.getAll(scopeRange(next));
      const keptViews = stores.views.getAll(scopeRange(next));
      keptViews.onsuccess = () => done({ meta: kept.result as ScopeMeta | undefined, rows: keptRows.result as RowRecord[], views: keptViews.result as ViewRecord[] });
    });
    if (scope !== next || !found) return;
    if (found.meta && now - found.meta.used_at > UNUSED_MS) {
      persist("expiry", (stores) => deleteScope(stores, next), next);
      const days = Math.floor((now - found.meta.used_at) / 86_400_000);
      note("cache_dropped", `cache: dropped ${slug}, unused for ${days} days`, { reason: "unused", days });
    } else {
      meta = { ...found.meta, ...meta!, roles: meta?.roles ?? found.meta?.roles ?? null, cursor: found.meta?.cursor ?? null };
      for (const record of found.rows) put(record.collection, record.id, record.row, record.as_of, false);
      for (const record of found.views) {
        const view = views.get(record.name);
        if (!view || view.at < record.at) views.set(record.name, { collection: record.collection, at: record.at, ids: record.ids, extra: record.extra });
      }
    }
    const saved = meta!;
    persist("meta", (stores) => stores.meta.put(saved, metaKey(next)), next);
    emit();
    const ms = Math.round(performance.now() - started);
    note("cache_open", `cache: opened ${slug}, ${found.rows.length} rows, ${found.views.length} views in ${ms} ms`, { rows: found.rows.length, views: found.views.length, ms });
  })().catch((e: unknown) => {
    note("cache_no_disk", `cache: no disk cache (${errorName(e)}), memory only`, { error: errorName(e) });
  });
  return loading;
}

/** Resolves when the open scope has its rows from the disk. */
export const whenOpen = () => loading;

/** At a sign-out seen by onAuthStateChanged: empty the memory, keep the disk. */
export function clearMemory(): void {
  if (scope === null && !rows.size && !views.size) return;
  forget();
  scope = null;
  meta = null;
  emit();
}

/** Delete the cache of the open scope on the disk and in memory; `close` refuses each later disk write of the scope. */
export async function dropScope(reason: string, close = false): Promise<void> {
  const key = scope;
  if (key === null) return;
  const count = [...rows.values()].reduce((sum, list) => sum + list.size, 0);
  forget();
  if (meta) meta = { ...meta, cursor: null };
  emit();
  persist("drop", (stores) => deleteScope(stores, key), null);
  if (close) closed.add(key);
  note("cache_dropped", `cache: dropped ${slugOf(key)}, ${count} rows (${reason})`, { reason, rows: count });
  await flush();
}

/** A new set of roles drops the kept data of the old roles (SO-4341). */
export async function checkRoles(roles: string[]): Promise<void> {
  await loading;
  if (!meta || scope === null) return;
  const next = [...roles].sort();
  const before = meta.roles;
  if (before && before.join(",") === next.join(",")) return;
  if (before) await dropScope("role_change");
  meta = { ...meta, roles: next };
  const saved = meta;
  persist("meta", (stores) => stores.meta.put(saved, metaKey(saved.scope)), saved.scope);
}

/** Put a row in memory unless the kept row has a newer as_of; true when the row changed. */
function put(collection: string, id: string, row: unknown, as_of: number, check = true): boolean {
  let list = rows.get(collection);
  if (!list) {
    list = new Map();
    rows.set(collection, list);
  }
  const old = list.get(id);
  if (old && old.as_of > as_of) return false;
  const json = check ? JSON.stringify(row) : undefined;
  if (old && check && (old.json ??= JSON.stringify(old.row)) === json) {
    old.as_of = as_of;
    return false;
  }
  list.set(id, { row, as_of, json });
  return true;
}

function writeRows(collection: string, changed: { id: string; row: unknown; as_of: number }[]) {
  const key = scope;
  if (key === null || !changed.length) return;
  persist(
    collection,
    (stores) => {
      for (const item of changed) stores.rows.put({ scope: key, collection, ...item } satisfies RowRecord, rowKey(key, collection, item.id));
    },
    key,
  );
}

/** Merge rows by id: the higher as_of wins. Only the changed rows go to the disk. */
export function merge<T extends { id: string }>(collection: string, list: T[], as_of: number): number {
  const changed = list.filter((row) => put(collection, row.id, row, as_of)).map((row) => ({ id: row.id, row: rows.get(collection)!.get(row.id)!.row, as_of }));
  writeRows(collection, changed);
  if (changed.length) emit();
  return changed.length;
}

/** Change a kept row with `patch`; nothing when the row is not kept or the kept row is newer. */
export function patch<T>(collection: string, id: string, change: (old: T) => T, as_of: number): boolean {
  const old = rows.get(collection)?.get(id);
  if (!old || old.as_of > as_of) return false;
  const row = change(old.row as T);
  if (!put(collection, id, row, as_of)) return false;
  writeRows(collection, [{ id, row, as_of }]);
  emit();
  return true;
}

/** Keep a list page; `complete` removes the rows of the collection that the list does not hold. */
export function setView<E>(name: string, collection: string, ids: string[], extra: E, at: number, complete = false): void {
  const old = views.get(name);
  if (old && old.at > at) return;
  const key = scope;
  views.set(name, { collection, at, ids, extra });
  const gone: string[] = [];
  if (complete) {
    const keep = new Set(ids);
    for (const id of rows.get(collection)?.keys() ?? []) if (!keep.has(id)) gone.push(id);
    for (const id of gone) rows.get(collection)!.delete(id);
  }
  emit();
  if (key === null) return;
  persist(
    `${name} view`,
    (stores) => {
      stores.views.put({ scope: key, name, collection, at, ids, extra } satisfies ViewRecord<E>, viewKey(key, name));
      for (const id of gone) stores.rows.delete(rowKey(key, collection, id));
    },
    key,
  );
}

/** Remove one view of the open scope from memory and from the disk; false when it is not kept. */
export function removeView(name: string): boolean {
  const key = scope;
  if (!views.delete(name)) return false;
  emit();
  if (key !== null) persist(`${name} view`, (stores) => stores.views.delete(viewKey(key, name)), key);
  return true;
}

/** Remove one id from a view, for a deleted record. */
export function removeFromView(name: string, id: string): void {
  const view = views.get(name);
  if (!view || !view.ids.includes(id)) return;
  setView(name, view.collection, view.ids.filter((item) => item !== id), view.extra, view.at);
}

/** The rows of a view in its order, or null when the view is not kept. */
export function readView<T, E>(name: string): { rows: T[]; extra: E; at: number } | null {
  const view = views.get(name);
  if (!view) return null;
  const list = rows.get(view.collection);
  const found = view.ids.flatMap((id) => {
    const entry = list?.get(id);
    return entry ? [entry.row as T] : [];
  });
  return { rows: found, extra: view.extra as E, at: view.at };
}

/** The names of the kept views of one collection. */
export function viewNames(collection: string): string[] {
  return [...views].filter(([, view]) => view.collection === collection).map(([name]) => name);
}

/** The kept state of the open scope, or null with no open scope. */
export const scopeMeta = (): ScopeMeta | null => meta;

/** The as_of of a row from a write answer: the change feed seq when the scope has one, else the clock. */
export const stamp = (): number => meta?.cursor ?? Date.now();

/** The sync engine sets the function that a write or a dropped cache calls to sync at once. */
export function setWaker(wake: ((reason: string) => void) | null): void {
  waker = wake;
}

export function wake(reason: string): void {
  waker?.(reason);
}

/** Change the kept state of the open scope, in memory and on the disk. */
export function setMeta(change: Partial<Omit<ScopeMeta, "scope">>): void {
  if (!meta || scope === null) return;
  meta = { ...meta, ...change };
  const saved = meta;
  persist("meta", (stores) => stores.meta.put(saved, metaKey(saved.scope)), saved.scope);
}

/** A new access key of the grants drops the kept rows of the old grants, as a role change does. */
export async function checkAccess(key: string | undefined, wakeUp = true): Promise<boolean> {
  await loading;
  if (!key || !meta || scope === null) return false;
  const before = meta.access_key;
  if (before === key) return false;
  if (before) await dropScope("access_key");
  setMeta({ access_key: key });
  if (before && wakeUp) wake("access_key");
  return Boolean(before);
}

/** The result of one feed merge: the rows that changed and the rows that went. */
export interface Applied {
  changed: number;
  removed: number;
}

/** Merge feed rows (the higher as_of wins) and remove `gone`; the cursor moves in memory after the disk write of rows and cursor. */
export async function applyRows<T extends { id: string }>(collection: string, viewName: string, list: T[], gone: string[], as_of: number, cursor: number): Promise<Applied | null> {
  const key = scope;
  if (key === null || !meta) return null;
  const changed = list.filter((row) => put(collection, row.id, row, as_of)).map((row) => ({ id: row.id, row: rows.get(collection)!.get(row.id)!.row, as_of }));
  const kept = rows.get(collection);
  const removed = gone.filter((id) => {
    const old = kept?.get(id);
    if (!old || old.as_of > as_of) return false;
    kept!.delete(id);
    return true;
  });
  const view = views.get(viewName);
  let saved: View | null = null;
  if (view) {
    const shown = new Set(view.ids);
    const away = new Set(removed);
    const added = changed.filter((item) => !shown.has(item.id)).map((item) => item.id);
    if (added.length || removed.some((id) => shown.has(id))) {
      saved = { ...view, ids: [...view.ids.filter((id) => !away.has(id)), ...added] };
      views.set(viewName, saved);
    }
  }
  if (changed.length || removed.length) emit();
  const next: ScopeMeta = { ...meta, cursor };
  await persistNow(
    collection,
    (stores) => {
      for (const item of changed) stores.rows.put({ scope: key, collection, ...item } satisfies RowRecord, rowKey(key, collection, item.id));
      for (const id of removed) stores.rows.delete(rowKey(key, collection, id));
      if (saved) stores.views.put({ scope: key, name: viewName, ...saved } satisfies ViewRecord, viewKey(key, viewName));
      stores.meta.put(next, metaKey(key));
    },
    key,
  );
  if (scope !== key) return null;
  meta = { ...meta, cursor };
  return { changed: changed.length, removed: removed.length };
}

/** Replace all rows of a collection and its view in one step: the page keeps the old rows until the swap. */
export async function swapRows<T extends { id: string }, E>(collection: string, viewName: string, list: T[], extra: E, at: number, as_of: number, change: Partial<Omit<ScopeMeta, "scope">>): Promise<boolean> {
  const key = scope;
  if (key === null || !meta) return false;
  const fresh = new Map<string, Entry>();
  for (const row of list) fresh.set(row.id, { row, as_of });
  rows.set(collection, fresh);
  const view: View<E> = { collection, at, ids: list.map((row) => row.id), extra };
  views.set(viewName, view);
  emit();
  const next: ScopeMeta = { ...meta, ...change };
  await persistNow(
    `${collection} swap`,
    (stores) => {
      stores.rows.delete(collectionRange(key, collection));
      for (const row of list) stores.rows.put({ scope: key, collection, id: row.id, row, as_of } satisfies RowRecord, rowKey(key, collection, row.id));
      stores.views.put({ scope: key, name: viewName, ...view } satisfies ViewRecord<E>, viewKey(key, viewName));
      stores.meta.put(next, metaKey(key));
    },
    key,
  );
  if (scope !== key) return false;
  meta = { ...meta, ...change };
  return true;
}

/** For tests: forget the memory and the boot check. */
export function resetForTests(): void {
  forget();
  scope = null;
  meta = null;
  booted = null;
  loading = Promise.resolve();
  pending.clear();
  closed.clear();
  waker = null;
}
